import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "tranos:list-filters:";

/**
 * Persists list filter state in sessionStorage so filters are kept when
 * switching between tabs (e.g. Transfers → Clients → back to Transfers).
 * Uses sessionStorage so filters reset when the browser tab is closed.
 */
export function usePersistedListFilters<T extends Record<string, unknown>>(
  pageKey: string,
  defaultFilters: T
): [T, (partial: Partial<T> | ((prev: T) => T)) => void] {
  const storageKey = `${STORAGE_PREFIX}${pageKey}`;

  const [filters, setFiltersState] = useState<T>(() => {
    if (typeof window === "undefined") return defaultFilters;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<T>;
        if (parsed && typeof parsed === "object") return { ...defaultFilters, ...parsed };
      }
    } catch {
      // ignore
    }
    return defaultFilters;
  });

  const setFilters = useCallback((partial: Partial<T> | ((prev: T) => T)) => {
    setFiltersState((prev) =>
      typeof partial === "function" ? partial(prev) : { ...prev, ...partial }
    );
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    } catch {
      // ignore quota / private mode
    }
  }, [filters, storageKey]);

  return [filters, setFilters];
}
